<script setup lang="ts">
import SeoHead from '@/components/marketing/SeoHead.vue';
import MarketingLayout from '@/layouts/MarketingLayout.vue';

const lastUpdated = 'August 2, 2026';

const sections = [
    {
        title: '1. Agreement',
        paragraphs: [
            'These Terms of Service govern your use of CreativeTint. By creating an account or using the service, you agree to them. If you are using CreativeTint on behalf of a company, you confirm you have authority to bind that company.',
        ],
    },
    {
        title: '2. Your account & teams',
        paragraphs: [
            'You are responsible for your account and for everything published through it. Keep your credentials secure. Team owners control who joins their workspace and what those members can do; content created in a team workspace is accessible to that team.',
        ],
    },
    {
        title: '3. Plans, credits & billing',
        items: [
            'CreativeTint has one paid plan (Pro). New workspaces receive a 7-day free trial with full Pro access. After the trial, Pro is billed monthly through Creem and renews automatically until cancelled.',
            'Manual creating, editing, scheduling, and publishing are unlimited on Pro. AI generation and Autopilot follow-ups consume monthly AI credits (currently 1,000 on Pro) under a fair-use policy that we may adjust over time.',
            'You can cancel at any time from Billing settings. If you cancel during the trial, you will not be charged. After you subscribe, cancellation takes effect at the end of the current billing period.',
        ],
    },
    {
        title: '4. Refunds',
        paragraphs: [
            'If CreativeTint is not working out within your first 14 days on a paid plan, email support@creativetint.com and we will refund your first payment — no questionnaire, no hoops. After that, payments for periods already started are non-refundable, but you keep access until the period ends.',
            'Credits consumed by failed generations are automatically refunded to your balance.',
        ],
    },
    {
        title: '5. Acceptable use',
        paragraphs: ['You agree not to use CreativeTint to:'],
        items: [
            'Publish spam, deceptive content, or anything that violates the rules of the platforms you publish to.',
            'Generate or distribute content that is illegal, infringing, hateful, or harassing.',
            'Impersonate others or misrepresent AI-generated content where disclosure is legally required.',
            'Probe, disrupt, or overload the service, or access it by any means other than the interfaces we provide.',
        ],
    },
    {
        title: '6. Your content & ownership',
        paragraphs: [
            'You own the content you bring to CreativeTint and the content you generate with it, to the fullest extent permitted by law. You grant us only the limited license needed to operate the service — storing your content, sending it to AI providers you invoke, and publishing it to the platforms you connect.',
            'We do not use your content to train models shared with other customers.',
        ],
    },
    {
        title: '7. AI-generated content',
        paragraphs: [
            'AI output can be wrong, outdated, or unintentionally similar to existing material. You are responsible for reviewing content before publishing it. Autopilot drafts are suggestions — nothing is published without a schedule you or your team created or approved.',
        ],
    },
    {
        title: '8. Third-party platforms',
        paragraphs: [
            'Publishing and analytics depend on the APIs of the social platforms you connect. Their terms apply to your use of those platforms, and their availability, rate limits, and policy changes are outside our control. If a platform suspends API access, related features may be degraded until access is restored.',
        ],
    },
    {
        title: '9. Availability & changes',
        paragraphs: [
            'We work hard to keep CreativeTint fast and available, but the service is provided "as is" without uptime guarantees on self-serve plans. We may add, change, or remove features; if we materially reduce what your paid plan includes, we will tell you in advance.',
        ],
    },
    {
        title: '10. Limitation of liability',
        paragraphs: [
            'To the maximum extent permitted by law, CreativeTint is not liable for indirect, incidental, or consequential damages — including lost profits, lost data, or platform account actions taken by third parties. Our total liability for any claim is limited to the amount you paid us in the twelve months before the claim.',
        ],
    },
    {
        title: '11. Termination',
        paragraphs: [
            'You can stop using CreativeTint and delete your account at any time. We may suspend or terminate accounts that violate these terms, with notice where practicable. On termination, you can export your content for 30 days before it is deleted.',
        ],
    },
    {
        title: '12. Changes to these terms',
        paragraphs: [
            'We may update these terms as the product evolves. For material changes we will notify you by email or in-app notice at least 14 days before they take effect. Continuing to use the service after that means you accept the new terms.',
        ],
    },
    {
        title: '13. Contact',
        paragraphs: [
            'Questions about these terms: legal@creativetint.com.',
        ],
    },
];
</script>

<template>
    <SeoHead
        title="Terms of Service — CreativeTint"
        description="CreativeTint Terms of Service: accounts, billing, acceptable use, content ownership, AI-generated content, and third-party platform policies."
        path="/terms"
        type="article"
    />
    <MarketingLayout>
        <div class="mx-auto max-w-3xl px-4 py-12 sm:px-6 sm:py-20">
            <p class="text-xs font-bold tracking-[0.2em] text-violet-500 dark:text-violet-300 uppercase">Legal</p>
            <h1 class="font-display mt-3 text-4xl font-extrabold tracking-tight md:text-5xl">Terms of Service</h1>
            <p class="mt-3 text-sm text-muted-foreground">Last updated: {{ lastUpdated }}</p>

            <p class="mt-8 text-lg leading-relaxed text-muted-foreground">
                The short version: you own your content, pay only for what your plan
                includes, get a 14-day refund window on your first payment, and
                agree to publish responsibly on the platforms you connect.
            </p>

            <div class="mt-12 space-y-10">
                <section v-for="s in sections" :key="s.title">
                    <h2 class="font-display text-xl font-bold tracking-tight">{{ s.title }}</h2>
                    <p
                        v-for="(p, i) in s.paragraphs ?? []"
                        :key="i"
                        class="mt-3 text-[15px] leading-relaxed text-muted-foreground"
                    >
                        {{ p }}
                    </p>
                    <ul v-if="s.items" class="mt-3 space-y-2.5">
                        <li
                            v-for="(item, i) in s.items"
                            :key="i"
                            class="flex items-start gap-2.5 text-[15px] leading-relaxed text-muted-foreground"
                        >
                            <span class="mt-2 size-1.5 flex-none rounded-full bg-violet-400" />
                            <span>{{ item }}</span>
                        </li>
                    </ul>
                </section>
            </div>
        </div>
    </MarketingLayout>
</template>
